/**
 * 权限同步 Wrapper 组件
 * 包裹需要鉴权的子组件，根据传入 props 判断同步的权限
 */
import React, { useState } from 'react';

import { Space } from '@arco-design/web-react';

import { PermissionRoleMapItem } from '@/utils/getAuthRelyOn';

import { PermissionSyncButton } from './PermissionSyncButton';
import { PermissionSyncPopconfirm } from './PermissionSyncPopconfirm';
import type { PermissionSyncResult, WithPermissionSyncConfig } from './types';
import { usePermissionSync } from './usePermissionSync';

export interface PermissionSyncWrapperProps {
  /** 权限配置，内部校验是否有按钮权限 */
  permission?: WithPermissionSyncConfig['permission'];
  /** 外部传入是否有权限，优先级高于 permission */
  hasPermission?: boolean;
  /** 所需接口权限 */
  requiredApis?: WithPermissionSyncConfig['requiredApis'];
  /** 子元素 */
  children: React.ReactNode;
  /** 无权限时是否隐藏，默认 true */
  hideWhenNoPermission?: boolean;
  /** 权限配置 */
  authMap?: PermissionRoleMapItem[];
  /** 样式 */
  style?: React.CSSProperties;
  /** 同步成功回调 */
  onSyncSuccess?: () => void;
  /** 同步失败回调 */
  onSyncError?: (error: Error) => void;
}

export const PermissionSyncWrapper: React.FC<PermissionSyncWrapperProps> = ({
  permission = null,
  hasPermission,
  requiredApis = {},
  children,
  hideWhenNoPermission = true,
  authMap,
  style = {},
  onSyncSuccess,
  onSyncError,
}) => {
  const [popupVisible, setPopupVisible] = useState(false);

  const syncResult: PermissionSyncResult = usePermissionSync({
    permission,
    hasPermission,
    requiredApis,
    authMap,
    onSyncSuccess,
    onSyncError,
  });

  // 1、无按钮权限时，默认不展示（返回隐藏占位元素，供父级 PermissionActionGroup 识别）
  if (!syncResult.hasPermission) {
    if (hideWhenNoPermission) {
      return <span data-permission-denied="true" style={{ display: 'none' }} />;
    }
    return <span style={{ opacity: 0.5, pointerEvents: 'none' }}>{children}</span>;
  }

  // 2、有权限且不需要同步时直接渲染
  if (!syncResult.needSync) {
    return <>{children}</>;
  }

  const handleSync = async () => {
    await syncResult.syncPermission();
  };

  // 有权限但需要同步：包裹内容，阻止子组件事件，点击 wrapper 弹出 Popconfirm
  return (
    <PermissionSyncPopconfirm visible={popupVisible} onClose={() => setPopupVisible(false)} onSync={handleSync}>
      <div
        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', ...style }}
        onClick={(e) => {
          e.stopPropagation();
          setPopupVisible(true);
        }}
      >
        <Space size={4} align="center">
          {/* 用 pointer-events-none 的 div 包裹 children，防止子组件绑定的事件生效 */}
          <div style={{ pointerEvents: 'none' }}>{children}</div>
          <PermissionSyncButton syncResult={syncResult} />
        </Space>
      </div>
    </PermissionSyncPopconfirm>
  );
};

export default PermissionSyncWrapper;
